import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

import { TranslocoPipe } from '@jsverse/transloco';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzTreeFlatDataSource, NzTreeFlattener, NzTreeViewModule } from 'ng-zorro-antd/tree-view';

import { UiIconsModule } from 'ui-icons';

import { FlatNode, TreeLeafData, TreeNode } from './tree-view.model';

import { UiPlatformButtonComponent } from '../button/button.component';

@Component({
  selector: 'ui-platform-tree-view',
  templateUrl: './tree-view.component.html',
  styleUrl: './tree-view.component.less',
  imports: [
    NzInputModule,
    NzTreeViewModule,
    NzToolTipModule,
    UiIconsModule,
    UiPlatformButtonComponent,
    TranslocoPipe
  ]
})
export class UiPlatformTreeViewComponent implements OnInit, OnChanges {
  @Input({ required: true }) treeData: Array<TreeNode> = [];
  @Input() selectedTreeNodes: Array<TreeNode> = [];
  @Input() canAddNew = false;
  @Input() addButtonLabel = 'general.add-custom-iro.title';

  @Output() selectedOptionsChange: EventEmitter<ReadonlyArray<TreeLeafData>> = new EventEmitter<ReadonlyArray<TreeLeafData>>();

  flatNodeMap = new Map<FlatNode, TreeNode>();
  nestedNodeMap = new Map<TreeNode, FlatNode>();
  selectListSelection = new SelectionModel<FlatNode>(true);

  treeControl = new FlatTreeControl<FlatNode>(
    node => node.level,
    node => node.expandable
  );

  treeFlattener = new NzTreeFlattener(
    this.#transformer.bind(this),
    node => node.level,
    node => node.expandable,
    node => node.children
  );

  dataSource = new NzTreeFlatDataSource(this.treeControl, this.treeFlattener);

  checklistSelection = new SelectionModel<FlatNode>(true);

  lastParentKeyMap = new Map<string, boolean>();
  childParentKeyMap = new Map<string, string>();
  childKeyParentNodeMap = new Map<string, FlatNode>();

  ngOnChanges({ treeData }: SimpleChanges): void {
    if (treeData?.currentValue) {
      this.#updateDataSourceAndHelperMaps(treeData?.currentValue);
    }
  }

  ngOnInit(): void {
    this.#expandTreeToLevel(2);
  }

  // Checks for deciding which node content to render (parent with toggle, checkmark, addbutton, etc)
  // it looks like these checks are done only on the first render,
  // so more dynamic stuff like if the node is toggled open won't have effect after the first check
  // ! Or maybe it does, when trackBy is fired
  hasChild = (_: number, node: FlatNode): boolean => {
    return node.expandable && (!this.lastParentKeyMap.get(node.key) || !this.canAddNew);
  };
  hasAddButton = (_: number, node: FlatNode): boolean => {
    return node.expandable && !!this.lastParentKeyMap.get(node.key) && this.canAddNew;
  };
  hasNoContent = (_: number, node: FlatNode): boolean => {
    return node.label === '' || node.state === 'draft';
  };

  trackBy = (_: number, node: FlatNode): string => `${node.key}-${node.label}`;

  onAddButtonClick(node: FlatNode, duplicate = false): void {
    const targetNode = duplicate && this.childKeyParentNodeMap.get(node.key) ? this.childKeyParentNodeMap.get(node.key)! : node;
    const nodeToNestIn = this.flatNodeMap.get(targetNode);

    if (nodeToNestIn) {
      nodeToNestIn.children = nodeToNestIn.children || [];
      nodeToNestIn.children.push({
        label: duplicate ? node.label : '',
        assetId: duplicate ? node.assetId : null,
        key: `${nodeToNestIn.key}-${duplicate ? 'DUPLICATE' : 'CUSTOM'}-${crypto.randomUUID()}`,
        state: 'draft' // for us to know if we should persist it or discard
      });
      this.#updateDataSourceAndHelperMaps(this.treeData);
      this.treeControl.expand(node);
    }
  }

  onSaveNodeClick(node: FlatNode, value: string): void {
    const nestedNode = this.flatNodeMap.get(node);
    if (nestedNode) {
      if (nestedNode.label === value) {
        return;
      }
      nestedNode.label = value;
      nestedNode.state = 'saved'; // for us to know if we should persist it or discard
      this.#updateDataSourceAndHelperMaps(this.treeData);
      this.itemSelectionToggle(node);
    }
  }

  onCancelNodeClick(node: FlatNode): void {
    const originNode = this.flatNodeMap.get(node);

    const findParentNode = (): TreeNode | null => {
      const treeCopy = [...this.treeData];

      while (treeCopy.length > 0) {
        // We are cutting the nodes to make the search easier
        const nodeToCheck = treeCopy.pop()!;

        if (nodeToCheck.children) {
          if (nodeToCheck.children.find(treeNodeChild => treeNodeChild === originNode)) {
            // If we find the parent node we return it
            return nodeToCheck;
          }
          // Otherwise we send the nested nodes to be tested
          treeCopy.push(...nodeToCheck.children);
        }
      }
      return null;
    };

    const parentNode = findParentNode();

    if (parentNode?.children) {
      parentNode.children = parentNode.children.filter(treeNodeChild => treeNodeChild !== originNode);
    }

    this.#updateDataSourceAndHelperMaps(this.treeData);
  }

  itemSelectionToggle(node: FlatNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);
    this.checklistSelection.isSelected(node) ? this.checklistSelection.select(...descendants) : this.checklistSelection.deselect(...descendants);

    descendants.forEach(child => this.checklistSelection.isSelected(child));

    const selectedNodes: ReadonlyArray<TreeLeafData> = this.checklistSelection.selected.map(selectedItem => {
      return {
        id: selectedItem.key,
        label: selectedItem.label,
        assetId: selectedItem.assetId,
        path: this.#createPath(selectedItem.key)
      };
    });

    this.selectedOptionsChange.emit(selectedNodes);
  }

  #transformer(node: TreeNode, level: number): FlatNode {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode =
      existingNode && existingNode.key === node.key
        ? existingNode
        : ({
            expandable: !!node.children,
            label: node.label,
            level,
            key: node.key,
            disabled: node.disabled,
            state: node.state,
            selected: node.selected,
            assetId: node.assetId
            // highlighted: node.highlighted
          } as FlatNode);
    flatNode.label = node.label;
    flatNode.state = node.state; // needed for the template checker
    // if (node.assetId === 'esg.iro.FACILITIES') {
    //   console.log('node', node);
    //   console.warn('flatNode', flatNode);
    // }
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  }

  // We need to hold some information about relations between the nodes, for easier traversal over the tree
  #updateHelperMaps(treeData: Array<TreeNode>, flattenedNodes?: Array<FlatNode>) {
    // We have to get the flat nodes (we are not interested in the tree nodes),
    // and it's sufficient to do it only at the root level, which will send it deeper, for better performance
    if (!flattenedNodes) {
      flattenedNodes = this.treeFlattener.flattenNodes(treeData);
    }

    treeData.forEach(node => {
      if (node.selected) {
        const flatNode = flattenedNodes?.find(flatNode => flatNode.key === node.key);
        if (flatNode) {
          this.checklistSelection.select(flatNode);
        }
      }
      if (!node.children) {
        return;
      }

      node.children.forEach(childNode => {
        this.childParentKeyMap.set(childNode.key, node.key);

        const parentFlatNode: FlatNode | undefined = flattenedNodes?.find(flatNode => flatNode.key === node.key);
        if (parentFlatNode) {
          this.childKeyParentNodeMap.set(childNode.key, parentFlatNode);
        }
      });

      this.#updateHelperMaps(node.children, flattenedNodes);

      // Last parent, node that has nodes which don't have any children (so we know where to show + Add IRO button)
      if (!node.children.some(child => child.children)) {
        this.lastParentKeyMap.set(node.key, true);
      }
    });
  }

  #updateDataSourceAndHelperMaps(treeData: Array<TreeNode>): void {
    this.dataSource.setData(treeData);
    this.#updateHelperMaps(treeData);
  }

  #createPath(childKey: string, path: Array<string> = []): ReadonlyArray<string> {
    const parent = this.childParentKeyMap.get(childKey);
    if (parent) {
      path.unshift(parent);
      this.#createPath(parent, path);
    }
    return path;
  }

  #expandTreeToLevel(level = 0): void {
    this.treeControl.expansionModel.select(...this.treeControl.dataNodes.filter((node: FlatNode) => node.level < level));
  }
}
